import moment from "moment";
import * as domain from "common/domain";
import actionTypes from "./_actions";
import farmHomeActionTypes from "containers/FarmHome/_actions";
import appActions from "containers/App/_actions";

const INITIAL_STATE = [];

const budgetsReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case appActions.APP_ONLINE: {
            //Any changes while done offline will trigger the isSaving flag. When coming back
            //online will block any other saves. The code below fixes this scenario
            return state.map((offlineFarm) => {
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    return {
                        ...offlineBudget,
                        isSyncing: false,
                        isSaving: false,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }
        case actionTypes.BUDGET_TOUCHED: {
            const { budget, isDirty = true, isNew = false, isDeleted = false, isSaving = false, isPendingSave = false } = action.payload;

            if (!budget.blocks || budget.blocks.filter((b) => b.type !== domain.BlockType.FodderCrop).length === 0) {
                budget.hasCompletedBlocks = false;
            }

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                const offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((ob) => {
                    if (ob.id !== budget.id) return ob;

                    return {
                        ...ob,
                        isNew: ob.isNew ? ob.isNew : isNew,
                        isDirty,
                        isDeleted,
                        isSaving,
                        isPendingSave,
                        budget: {
                            ...budget,
                            versionId: ob.versionId,
                        },
                    };
                });

                if (!offlineBudgets.some((b) => b.id === budget.id)) {
                    offlineBudgets.push({
                        id: budget.id,
                        farmId: budget.farmId,
                        isDownloaded: true,
                        isDirty,
                        isNew,
                        isSaving,
                        isPendingSave,
                        lastFetchedTimestamp: moment().valueOf(),
                        budget,
                    });
                }

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_SYNCING_BEGIN: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        isSyncing: true,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_SYNCING_END: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        isSyncing: false,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_FETCHING_BEGIN: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        isFetching: true,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_FETCHING_END: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        isFetching: false,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_EXECUTE_FETCHED: {
            const { budget } = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                const offlineBudgets = offlineFarm.farm.offlineBudgets.map((ob) => {
                    if (ob.id !== budget.id) return ob;

                    return {
                        ...ob,
                        versionId: budget.versionId,
                        budget: budget,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_FETCHED: {
            const { budget, userId } = action.payload;

            const offlineBudget = {
                id: budget.id,
                farmId: budget.farmId,
                versionId: budget.versionId,
                isDirty: false,
                isNew: false,
                isDownloaded: true,
                isSyncing: false,
                lastFetchedTimestamp: moment().valueOf(),
                budget,
            };

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== offlineBudget.farmId) return offlineFarm;

                const offlineBudgets = offlineFarm.farm.offlineBudgets.map((ob) => {
                    if (ob.id !== offlineBudget.id) return ob;

                    if (ob.isDownloaded && ob.versionId === offlineBudget.versionId) {
                        return ob;
                    }

                    if (ob.isDirty) {
                        if (budget.lastUpdatedBy === userId) {
                            return {
                                ...ob,
                                budget: {
                                    ...ob.budget,
                                    versionId: offlineBudget.versionId,
                                },
                            };
                        } else {
                            return ob;
                        }
                    }
                    offlineBudget.budget.datasetName = ob.budget.datasetName;
                    return offlineBudget;
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_UNDO_CHANGES: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        isDirty: false,
                        isNew: false,
                    };
                });

                // Sync deleted.
                if (offlineBudgets.some((ob) => ob.id === budget.id && ob.isDeleted === true)) offlineBudgets = offlineBudgets.filter((ob) => ob.id !== budget.id);

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_SAVED: {
            const budget = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                // Sync dirty.
                let offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    // Clear regenerateSoils from any blocks
                    return {
                        ...offlineBudget,
                        budget: {
                            ...offlineBudget.budget,
                            blocks: (offlineBudget.budget.blocks || []).map((block) => (block.regenerateSoils ? { ...block, regenerateSoils: null } : block)),
                        },
                        isDirty: false,
                        isNew: false,
                        isSaving: false,
                    };
                });

                // Sync deleted.
                if (offlineBudgets.some((ob) => ob.id === budget.id && ob.isDeleted === true)) offlineBudgets = offlineBudgets.filter((ob) => ob.id !== budget.id);

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case actionTypes.BUDGET_SAVE_ERROR: {
            const { budget, error } = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                const offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    const isTimeout = error.name === "TimeoutError";

                    return {
                        ...offlineBudget,
                        isSaving: false,
                        error: {
                            requestId: ((error.xhr || {}).response || {}).requestId,
                            status: isTimeout ? 408 : error.status,
                            message: error.message,
                        },
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        case farmHomeActionTypes.GET_BLOCK_REGION_BENCHMARKS_SUCCESS: {
            const { budget, blockBenchmarks } = action.payload;

            return state.map((offlineFarm) => {
                if (offlineFarm.id !== budget.farmId) return offlineFarm;

                const offlineBudgets = (offlineFarm.farm.offlineBudgets || []).map((offlineBudget) => {
                    if (offlineBudget.id !== budget.id) return offlineBudget;

                    return {
                        ...offlineBudget,
                        blockBenchmarks,
                    };
                });

                return {
                    ...offlineFarm,
                    farm: {
                        ...offlineFarm.farm,
                        offlineBudgets,
                    },
                };
            });
        }

        default:
            return state;
    }
};

export default budgetsReducer;
